import React from "react";
import {
  DataTable,
  DataTableStateEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { RFMSegmentationData } from "../../../models/customer-analytics/customer-analytics.model";
import CallStatusCell from "../shared/call-status-cell";

interface RFMDataTableProps {
  data: RFMSegmentationData[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  sortField: string;
  sortOrder: 1 | -1;
  onPageChange: (event: DataTableStateEvent) => void;
  onSortChange: (field: string, order: string) => void;
  refreshData: () => void;
}

const getSegmentColor = (segment: string): { bg: string; text: string } => {
  const map: Record<string, { bg: string; text: string }> = {
    Champions: { bg: "#22c55e", text: "#fff" },
    "Loyal Customers": { bg: "#3b82f6", text: "#fff" },
    "New Customers": { bg: "#f59e0b", text: "#1e293b" },
    "At Risk - High Value": { bg: "#ef4444", text: "#fff" },
    "At Risk - Low Value": { bg: "#fca5a5", text: "#1e293b" },
    "Big Spenders (Infrequent)": { bg: "#a855f7", text: "#fff" },
    "Lost Customers": { bg: "#94a3b8", text: "#fff" },
    "Need Attention": { bg: "#f97316", text: "#fff" },
  };
  return map[segment] || { bg: "#e2e8f0", text: "#1e293b" };
};

const RFMDataTable: React.FC<RFMDataTableProps> = (props) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const onSort = (e: DataTableSortEvent) => {
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    props.onSortChange(e.sortField, order);
  };

  const segmentBody = (rowData: RFMSegmentationData) => {
    const colors = getSegmentColor(rowData.customerSegment);
    return (
      <span
        className="ca-badge"
        style={{ backgroundColor: colors.bg, color: colors.text }}
      >
        {rowData.customerSegment}
      </span>
    );
  };

  const scoreBadge = (value: number) => (
    <span className="ca-score-badge">{value}</span>
  );

  const rfmTotalBadge = (value: number) => (
    <span className="ca-rfm-total-badge">{value}</span>
  );

  const callDataBody = (rowData: RFMSegmentationData) => (
    <CallStatusCell callDataJson={rowData.callData} />
  );

  return (
    <div className="col-12">
      <div className="card border-noround p-2">
        <DataTable
          value={props.data}
          paginator
          rows={props.pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          totalRecords={props.totalRecords}
          lazy
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          first={(props.pageNumber - 1) * props.pageSize}
          onPage={props.onPageChange}
          sortMode="single"
          sortField={props.sortField}
          sortOrder={props.sortOrder}
          onSort={onSort}
          scrollable
          scrollHeight="flex"
          responsiveLayout="scroll"
          emptyMessage="No records found"
          className="ca-datatable"
        >
          <Column field="customerName" header="Customer" />
          <Column field="primaryContactNbr" header="Phone Number" />
          <Column field="recency" header="Recency" sortable />
          <Column field="frequency" header="Frequency" sortable />
          <Column
            field="monetary"
            header="Monetary"
            body={(row) => formatCurrency(row.monetary)}
            sortable
          />
          <Column
            field="recencyScore"
            header="R"
            body={(row) => scoreBadge(row.recencyScore)}
            sortable
          />
          <Column
            field="frequencyScore"
            header="F"
            body={(row) => scoreBadge(row.frequencyScore)}
            sortable
          />
          <Column
            field="monetaryScore"
            header="M"
            body={(row) => scoreBadge(row.monetaryScore)}
            sortable
          />
          <Column
            field="rfmTotal"
            header="RFM Total"
            body={(row) => rfmTotalBadge(row.rfmTotal)}
            sortable
          />
          <Column header="Segment" body={segmentBody} style={{ minWidth: "150px" }} />
          <Column header="Last Call Status" body={callDataBody} style={{ minWidth: "140px" }} />
        </DataTable>
      </div>
    </div>
  );
};

export default RFMDataTable;
